/**
 * `/issues/reports` — «مشکلات پرتکرار».
 *
 * THREE NUMBERS, and each answers a different question:
 *
 *  - **count** — how many issues were REPORTED in the window. What people mean
 *    by "how many problems did we have".
 *  - **median time to resolve** — over issues RESOLVED in it. A median rather
 *    than a mean, because one issue that sat open over Nowruz would wreck an
 *    average and describe nobody's experience.
 *  - **reopen rate** — reopenings per resolution, counted from the append-only
 *    history rather than from current state. A high one means things are being
 *    closed before they are fixed, which no count would show.
 *
 * THE MONTH LABELS ARE JALALI AND THE CONVERSION HAPPENS HERE. The service
 * sends ISO bucket dates on purpose — a calendar is a locale decision and a
 * domain service does not make one (CLAUDE.md §12).
 */
import { useQuery } from '@apollo/client';
import { BarChart, Card, Spinner, TBody, TD, TH, THead, TR, Table } from '@meshhub/design-tokens/ui';
import { ArrowRight } from 'lucide-react';
import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { MutationError } from '../../components/FormKit.web.jsx';
import { formatCount, formatJalaliMonth } from '../../lib/format.js';
import { ISSUE_KIND_LABELS, ISSUE_PRIORITY_LABELS } from '../../lib/issueLabels.js';
import { ISSUE_STATS_QUERY } from '../../lib/queries.js';
import { S } from '../../lib/strings.js';

const GROUPS = [
  { value: 'TYPE', label: S.issues.reportByType },
  { value: 'KIND', label: S.issues.reportByKind },
  { value: 'PRIORITY', label: S.issues.reportByPriority },
  { value: 'ASSIGNEE', label: S.issues.reportByAssignee },
];

const BUCKETS = [
  { value: 'NONE', label: S.issues.reportNone },
  { value: 'MONTH', label: S.issues.reportMonthly },
  { value: 'WEEK', label: S.issues.reportWeekly },
];

/** A year back, which is what «مشکلات پرتکرار» is usually asked about. */
function defaultRange() {
  const to = new Date();
  const from = new Date(to);
  from.setFullYear(from.getFullYear() - 1);
  return [from.toISOString(), to.toISOString()];
}

export function IssueReportsScreen() {
  const [groupBy, setGroupBy] = useState('TYPE');
  const [bucket, setBucket] = useState('NONE');
  const [dateFrom, dateTo] = useMemo(defaultRange, []);

  const { data, loading, error } = useQuery(ISSUE_STATS_QUERY, {
    variables: { dateFrom, dateTo, groupBy, bucket },
  });
  const buckets = data?.issueStats ?? [];

  /** The label a row reads under, once the enum and the calendar are applied. */
  const labelFor = (row) => {
    const base =
      groupBy === 'KIND'
        ? (ISSUE_KIND_LABELS[row.label] ?? row.label)
        : groupBy === 'PRIORITY'
          ? (ISSUE_PRIORITY_LABELS[row.label] ?? row.label)
          : row.label || S.app.none;
    if (bucket === 'NONE' || !row.bucketStart) return base;
    return `${formatJalaliMonth(row.bucketStart)} · ${base}`;
  };

  return (
    <div className="flex h-screen flex-col">
      <header
        className="flex h-toolbar shrink-0 items-center gap-3 border-b border-border
                   bg-surface px-6"
      >
        <Link to="/issues" className="text-text-muted">
          <ArrowRight size={16} aria-hidden="true" />
        </Link>
        <h1 className="text-lg font-bold">{S.issues.reports}</h1>

        <select
          value={groupBy}
          onChange={(event) => setGroupBy(event.target.value)}
          className="h-control rounded border border-border bg-bg px-3 text-base"
          aria-label={S.issues.reportByType}
        >
          {GROUPS.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
        <select
          value={bucket}
          onChange={(event) => setBucket(event.target.value)}
          className="h-control rounded border border-border bg-bg px-3 text-base"
          aria-label={S.issues.reportBucket}
        >
          {BUCKETS.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </header>

      <div className="min-h-0 flex-1 overflow-auto p-6">
        <MutationError error={error} />
        {loading && <Spinner />}

        {!loading && (
          <div className="flex flex-col gap-6">
            <Card className="flex flex-col gap-4">
              <h2 className="text-md font-bold">{S.issues.reportCount}</h2>
              <BarChart
                data={buckets.map((row) => ({
                  key: row.key,
                  label: labelFor(row),
                  value: row.count,
                }))}
                valueLabel={S.issues.reportCount}
                formatValue={(value) => formatCount(value)}
                emptyLabel={S.issues.noIssues}
              />
            </Card>

            <Card className="flex flex-col gap-3">
              <Table>
                <THead>
                  <TR>
                    <TH>{S.issues.reportBucket}</TH>
                    <TH>{S.issues.reportCount}</TH>
                    <TH>{S.issues.statusResolved}</TH>
                    <TH>{S.issues.reportMedian}</TH>
                    <TH>{S.issues.reportReopen}</TH>
                  </TR>
                </THead>
                <TBody>
                  {buckets.map((row) => (
                    <TR key={row.key}>
                      <TD className="font-medium">{labelFor(row)}</TD>
                      <TD className="tabular-nums">{formatCount(row.count)}</TD>
                      <TD className="tabular-nums">{formatCount(row.resolvedCount)}</TD>
                      <TD className="tabular-nums">
                        {/* null means NOTHING in this bucket was resolved —
                            distinct from 0, which would claim instant
                            resolution. */}
                        {row.medianHoursToResolve === null
                          ? S.app.none
                          : formatCount(Math.round(row.medianHoursToResolve))}
                      </TD>
                      <TD className="tabular-nums">
                        {row.resolvedCount > 0
                          ? `${formatCount(
                              Math.round((row.reopenedCount / row.resolvedCount) * 100),
                            )}٪`
                          : S.app.none}
                      </TD>
                    </TR>
                  ))}
                </TBody>
              </Table>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
}
